/**
 * Composant tableau d'emploi du temps d'examens.
 * Affiche le planning au format grille : lignes = date+créneau, colonnes = groupes d'options.
 * Supporte l'édition manuelle (déplacement par flèches, verrouillage, suppression).
 * Les matières en tronc commun sont déplacées ensemble après confirmation.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Loader2, AlertCircle, Trash2, ArrowRightLeft, X, Check, Lock, EyeOff, Eye,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Layers, UserMinus, UserPlus, Users,
  Plus, Search, Info, AlertTriangle, Clock,
} from 'lucide-react';
import {
  useGetEmploiTempsQuery,
  useDeplacerCreneauMutation,
  useDeplacerGroupeMutation,
  useDeleteCreneauMutation,
  useVerrouillerCreneauMutation,
  useToggleInactifMutation,
  useModifierEnseignantCreneauMutation,
  useLazyGetMatiereManquantesQuery,
  useAjouterMatiereMutation,
  useFusionnerGroupesMutation,
  useModifierHorairesJourMutation,
  useEchangerGroupesMutation,
  EmploiTempsComplet,
  CreneauMatiereData,
  MatiereManquante,
} from '../../services/emploisTempsApi';
import { useGetFormateursQuery, Formateur } from '../../services/formateursApi';

interface Props {
  sessionId: number;
  niveau: string;
  pole: string;
  isDark: boolean;
  showEnseignants?: boolean;
  printMode?: boolean;
}

const EmploiTempsTableau: React.FC<Props> = ({
  sessionId, niveau, pole, isDark, showEnseignants: showEnseignantsProp, printMode = false
}) => {
  const [showEnseignants, setShowEnseignants] = useState(showEnseignantsProp ?? false);
  const [editMode, setEditMode] = useState(false);
  const [showAjoutModal, setShowAjoutModal] = useState(false);
  const [showFusionModal, setShowFusionModal] = useState(false);

  const { data, isLoading, error, refetch } = useGetEmploiTempsQuery(
    { sessionId, niveau, pole },
    { skip: !sessionId }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`flex items-center justify-center gap-2 py-16 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <AlertCircle size={20} />
        <span>Aucun emploi du temps disponible pour {niveau} — {pole}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Contrôles */}
      {!printMode && (
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showEnseignants}
              onChange={(e) => setShowEnseignants(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm">Afficher les noms des formateurs</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editMode}
              onChange={(e) => setEditMode(e.target.checked)}
              className="w-4 h-4 text-orange-600 rounded"
            />
            <span className="text-sm font-medium text-orange-600">Mode edition</span>
          </label>

          {editMode && (
            <span className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
              Utilisez les fleches pour deplacer les matieres
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            {data.groupes.length >= 2 && (
              <button
                onClick={() => setShowFusionModal(true)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600 text-purple-400' : 'bg-purple-50 hover:bg-purple-100 text-purple-700'
                }`}
              >
                <Layers size={14} />
                Fusionner groupes
              </button>
            )}
            <button
              onClick={() => setShowAjoutModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors"
            >
              <Plus size={14} />
              Ajouter une matière
            </button>
          </div>
        </div>
      )}

      {/* Modal ajout matière */}
      {showAjoutModal && (
        <AjouterMatiereModal
          sessionId={sessionId}
          niveau={niveau}
          isDark={isDark}
          jours={data.jours}
          onClose={() => setShowAjoutModal(false)}
          onSuccess={() => { setShowAjoutModal(false); refetch(); }}
        />
      )}

      {/* Modal fusion groupes */}
      {showFusionModal && (
        <FusionGroupesModal
          groupes={data.groupes}
          isDark={isDark}
          onClose={() => setShowFusionModal(false)}
          onSuccess={() => { setShowFusionModal(false); refetch(); }}
        />
      )}

      {/* Stats */}
      {!printMode && data.statistiques && (
        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {data.statistiques.nombre_groupes} groupes · {data.statistiques.nombre_creneaux} créneaux · {data.statistiques.nombre_jours} jours
        </div>
      )}

      {/* Tableau */}
      <TableauEmploiTemps
        data={data}
        sessionId={sessionId}
        niveau={niveau}
        showEnseignants={showEnseignants}
        isDark={isDark}
        printMode={printMode}
        editMode={editMode}
        onRefresh={refetch}
      />
    </div>
  );
};

// ─── Types internes ────────────────────────────────────────────────

/** Position d'un créneau dans la grille (jour + slot horaire) */
interface SlotPosition {
  jourIdx: number;
  creneauIdx: number;
  date: string;
  heure_label: string;
}

interface TableauProps {
  data: EmploiTempsComplet;
  sessionId: number;
  niveau: string;
  showEnseignants: boolean;
  isDark: boolean;
  printMode: boolean;
  editMode: boolean;
  onRefresh: () => void;
}

const TableauEmploiTemps: React.FC<TableauProps> = ({
  data, sessionId, niveau, showEnseignants, isDark, printMode, editMode, onRefresh,
}) => {
  const [selectedCreneau, setSelectedCreneau] = useState<{
    creneauId: number; matiereNom: string; isInactif: boolean;
    sourceDate: string; sourceHeure: string; nbShared: number;
  } | null>(null);
  const [moveTarget, setMoveTarget] = useState<{ date: string; heure: string } | null>(null);
  // Edition directe (date libre + heure au choix) — permet de sortir de la grille
  // des slots existants pour placer sur n'importe quel jour + heure standard.
  const [directDate, setDirectDate] = useState<string>('');
  const [directHeure, setDirectHeure] = useState<string>('');
  const [conflitMove, setConflitMove] = useState<{
    matiereSource: string;
    nbGroupesSource: number;
    sourceDate: string;
    sourceHeure: string;
    matiereConflit: string;
    targetDate: string;
    targetHeureDebut: string;
    targetHeureFin: string;
    slotLibreChoisi: string; // "date|heureDebut-heureFin"
    /** IDs des groupes concernés par le swap (colonnes à échanger). */
    groupeIds: number[];
  } | null>(null);
  const [confirmMove, setConfirmMove] = useState<{
    matiereNom: string;
    nbGroupes: number;
    sourceDate: string;
    sourceHeure: string;
    targetDate: string;
    targetHeureDebut: string;
    targetHeureFin: string;
    direction: string;
  } | null>(null);
  const [movingArrow, setMovingArrow] = useState(false);
  const [horairesJourCible, setHorairesJourCible] = useState<EmploiTempsComplet['jours'][0] | null>(null);

  const [deplacerCreneau, { isLoading: moving }] = useDeplacerCreneauMutation();
  const [deplacerGroupe] = useDeplacerGroupeMutation();
  const [echangerGroupes] = useEchangerGroupesMutation();
  const [deleteCreneau] = useDeleteCreneauMutation();
  const [verrouillerCreneau] = useVerrouillerCreneauMutation();
  const [toggleInactif] = useToggleInactifMutation();
  const [modifierEnseignant] = useModifierEnseignantCreneauMutation();
  const { data: formateurs = [] } = useGetFormateursQuery({ actif: true });

  // État pour le popup d'édition des formateurs
  const [editingFormateur, setEditingFormateur] = useState<{
    creneauId: number;
    matiereNom: string;
    enseignantNoms: string[];
    position: { top: number; left: number };
  } | null>(null);
  const [selectedFormateurId, setSelectedFormateurId] = useState<number | string>('');

  const borderColor = printMode ? 'border-black' : isDark ? 'border-gray-600' : 'border-gray-300';
  const headerBg = printMode ? 'bg-orange-100' : isDark ? 'bg-gray-700' : 'bg-orange-50';
  const cellBg = printMode ? 'bg-white' : isDark ? 'bg-gray-800' : 'bg-white';
  const textColor = printMode ? 'text-black' : isDark ? 'text-white' : 'text-gray-900';
  const subTextColor = printMode ? 'text-gray-600' : isDark ? 'text-gray-400' : 'text-gray-500';

  /**
   * Parse heure de début depuis label. Supporte les formats :
   *  - "08H-10H"       → "08:00"
   *  - "10H30-12H30"   → "10:30"
   *  - "08H00-10H00"   → "08:00"
   */
  const parseHeureDebut = (label: string): string => {
    const match = label.match(/^(\d{1,2})H(\d{0,2})/);
    if (!match) return '08:00';
    const h = match[1].padStart(2, '0');
    const m = (match[2] || '00').padStart(2, '0');
    return `${h}:${m}`;
  };

  const parseHeureFin = (label: string): string => {
    const parts = label.split('-');
    if (parts.length >= 2) {
      const match = parts[1].match(/^(\d{1,2})H(\d{0,2})/);
      if (!match) return '16:00';
      const h = match[1].padStart(2, '0');
      const m = (match[2] || '00').padStart(2, '0');
      return `${h}:${m}`;
    }
    return '10:00';
  };

  // Slots horaires : dérivés du data (union des heures affichées dans le tableau)
  // au lieu d'un fallback hardcodé — évite les orphelins quand la pause change
  // (ex: pause 30min → 08H, 10H30, 13H au lieu de 08H, 11H, 14H hardcodés).
  const defaultHeures = useMemo(() => {
    const seen = new Map<string, { label: string; debut: string; fin: string }>();
    data.jours.forEach((jour) => {
      jour.creneaux.forEach((c) => {
        // heure_label est de la forme "08H00-10H00" ou "08H-10H"
        const debut = parseHeureDebut(c.heure_label);
        const finMatch = c.heure_label.match(/-(\d{1,2}H(?:\d{0,2})?)/);
        const finRaw = finMatch ? finMatch[1] : '';
        // Convertir "10H00" → "10:00" et "10H30" → "10:30"
        const toColon = (s: string) => {
          const m = s.match(/^(\d{1,2})H(\d{0,2})$/);
          if (!m) return s;
          const h = m[1].padStart(2, '0');
          const mm = (m[2] || '00').padStart(2, '0');
          return `${h}:${mm}`;
        };
        const fin = toColon(finRaw);
        if (debut && fin && !seen.has(debut)) {
          seen.set(debut, { label: c.heure_label, debut, fin });
        }
      });
    });
    const list = Array.from(seen.values()).sort((a, b) => a.debut.localeCompare(b.debut));
    // Fallback si data vide (pas d'EDT généré) → slots classiques
    if (list.length === 0) {
      return [
        { label: '08H-10H', debut: '08:00', fin: '10:00' },
        { label: '11H-13H', debut: '11:00', fin: '13:00' },
        { label: '14H-16H', debut: '14:00', fin: '16:00' },
      ];
    }
    return list;
  }, [data.jours]);

  // Construire un index plat de toutes les positions
  const allPositions: SlotPosition[] = [];
  data.jours.forEach((jour, ji) => {
    jour.creneaux.forEach((_, ci) => {
      allPositions.push({ jourIdx: ji, creneauIdx: ci, date: jour.date, heure_label: jour.creneaux[ci].heure_label });
    });
  });

  // Slots disponibles pour le select de déplacement
  const availableSlots = data.jours.flatMap(jour =>
    defaultHeures.map(h => ({
      date: jour.date,
      heure_label: `${jour.jour_semaine} ${jour.date_label} ${h.label}`,
      heure_debut: h.debut,
      heure_fin: h.fin,
    }))
  );

  // Options d'heures uniques (pour l'édition date directe)
  const heureOptions = defaultHeures.map(h => ({
    label: h.label,
    debut: h.debut,
    fin: h.fin,
    value: `${h.debut}|${h.fin}`,
  }));

  /**
   * Compte combien de groupes partagent la même matière au même créneau (tronc commun).
   */
  const countSharedGroups = useCallback((matiereNom: string, jourIdx: number, creneauIdx: number): number => {
    const creneau = data.jours[jourIdx]?.creneaux[creneauIdx];
    if (!creneau) return 0;
    let count = 0;
    for (const groupe of data.groupes) {
      const mat = creneau.matieres[groupe.id];
      if (mat && mat.matiere_nom === matiereNom) count++;
    }
    return count;
  }, [data]);

  // parseHeureDebut / parseHeureFin sont définis plus haut (avant defaultHeures)

  /**
   * Cherche si un déplacement de `matiereNom` vers (targetDate, targetHeure)
   * entrerait en conflit avec une matière déjà présente sur les mêmes groupes.
   * Retourne le nom de la matière en conflit (unique attendu si tronc commun)
   * et la liste des groupes impactés, ou null si aucun conflit.
   */
  const detecterConflit = useCallback((
    matiereNom: string,
    sourceDate: string,
    sourceHeure: string,
    targetDate: string,
    targetHeureDebut: string,
  ): { matiereConflit: string; groupesConflit: number[] } | null => {
    // Trouver les groupes qui portent la matière source à sa position actuelle
    const jourSrc = data.jours.find(j => j.date === sourceDate);
    if (!jourSrc) return null;
    const creneauSrc = jourSrc.creneaux.find(c => parseHeureDebut(c.heure_label) === sourceHeure);
    if (!creneauSrc) return null;
    const groupesSource: number[] = [];
    for (const g of data.groupes) {
      const m = creneauSrc.matieres[g.id];
      if (m && m.matiere_nom === matiereNom) groupesSource.push(g.id);
    }
    if (!groupesSource.length) return null;

    // Vérifier ce qu'il y a sur la cible pour ces mêmes groupes
    const jourTgt = data.jours.find(j => j.date === targetDate);
    if (!jourTgt) return null;
    const creneauTgt = jourTgt.creneaux.find(c => parseHeureDebut(c.heure_label) === targetHeureDebut);
    if (!creneauTgt) return null;
    const matieresConflit = new Set<string>();
    const groupesEnConflit: number[] = [];
    for (const gid of groupesSource) {
      const mat = creneauTgt.matieres[gid];
      if (mat && mat.matiere_nom !== matiereNom) {
        matieresConflit.add(mat.matiere_nom);
        groupesEnConflit.push(gid);
      }
    }
    if (!matieresConflit.size) return null;
    // Généralement une seule matière en conflit sur tous ces groupes (tronc commun).
    // Si plusieurs → on renvoie la première (l'utilisateur devra faire au cas par cas).
    return {
      matiereConflit: Array.from(matieresConflit)[0],
      groupesConflit: groupesEnConflit,
    };
  }, [data]);

  /**
   * Cherche des créneaux libres compatibles pour déplacer la matière en
   * conflit ailleurs (au lieu de la swap). Un slot est « libre pour cette
   * matière » quand aucune des cellules des groupes cibles n'est occupée.
   */
  const trouverSlotsLibres = useCallback((matiereNom: string): Array<{
    date: string; heureDebut: string; heureFin: string; label: string;
  }> => {
    // Groupes qui portent cette matière quelque part
    const groupesConcernes = new Set<number>();
    let sourceDate = '', sourceHeure = '';
    for (const jour of data.jours) {
      for (const c of jour.creneaux) {
        for (const g of data.groupes) {
          const m = c.matieres[g.id];
          if (m && m.matiere_nom === matiereNom) {
            groupesConcernes.add(g.id);
            if (!sourceDate) { sourceDate = jour.date; sourceHeure = parseHeureDebut(c.heure_label); }
          }
        }
      }
    }
    if (!groupesConcernes.size) return [];
    const groupesArr = Array.from(groupesConcernes);
    const libres: Array<{ date: string; heureDebut: string; heureFin: string; label: string }> = [];
    for (const jour of data.jours) {
      for (const c of jour.creneaux) {
        const heureDebut = parseHeureDebut(c.heure_label);
        if (jour.date === sourceDate && heureDebut === sourceHeure) continue;
        let libre = true;
        for (const gid of groupesArr) {
          if (c.matieres[gid]) { libre = false; break; }
        }
        if (libre) {
          libres.push({
            date: jour.date,
            heureDebut,
            heureFin: parseHeureFin(c.heure_label),
            label: `${jour.jour_semaine} ${jour.date_label} · ${c.heure_label}`,
          });
        }
      }
    }
    return libres;
  }, [data]);

  /**
   * Calcule la position cible pour un déplacement par flèche.
   */
  const getTargetSlot = (
    jourIdx: number, creneauIdx: number, direction: 'up' | 'down' | 'left' | 'right'
  ): { jourIdx: number; creneauIdx: number } | null => {
    const jour = data.jours[jourIdx];
    if (!jour) return null;

    if (direction === 'up') {
      if (creneauIdx > 0) return { jourIdx, creneauIdx: creneauIdx - 1 };
      // Aller au dernier créneau du jour précédent
      if (jourIdx > 0) {
        const prevJour = data.jours[jourIdx - 1];
        return { jourIdx: jourIdx - 1, creneauIdx: prevJour.creneaux.length - 1 };
      }
      return null;
    }

    if (direction === 'down') {
      if (creneauIdx < jour.creneaux.length - 1) return { jourIdx, creneauIdx: creneauIdx + 1 };
      // Aller au premier créneau du jour suivant
      if (jourIdx < data.jours.length - 1) return { jourIdx: jourIdx + 1, creneauIdx: 0 };
      return null;
    }

    if (direction === 'left') {
      if (jourIdx > 0) return { jourIdx: jourIdx - 1, creneauIdx: Math.min(creneauIdx, data.jours[jourIdx - 1].creneaux.length - 1) };
      return null;
    }

    if (direction === 'right') {
      if (jourIdx < data.jours.length - 1) return { jourIdx: jourIdx + 1, creneauIdx: Math.min(creneauIdx, data.jours[jourIdx + 1].creneaux.length - 1) };
      return null;
    }

    return null;
  };

  /**
   * Déclenche le déplacement par flèche (avec détection tronc commun).
   */
  const handleArrowMove = (
    matiereNom: string,
    creneauId: number,
    jourIdx: number,
    creneauIdx: number,
    direction: 'up' | 'down' | 'left' | 'right'
  ) => {
    const target = getTargetSlot(jourIdx, creneauIdx, direction);
    if (!target) return;

    const targetJour = data.jours[target.jourIdx];
    const targetCreneau = targetJour.creneaux[target.creneauIdx];
    const sourceJour = data.jours[jourIdx];
    const sourceCreneau = sourceJour.creneaux[creneauIdx];

    const targetHeureDebut = parseHeureDebut(targetCreneau.heure_label);
    const targetHeureFin = parseHeureFin(targetCreneau.heure_label);
    const sourceHeureDebut = parseHeureDebut(sourceCreneau.heure_label);

    const nbGroupes = countSharedGroups(matiereNom, jourIdx, creneauIdx);

    const dirLabel = direction === 'up' ? 'haut' : direction === 'down' ? 'bas' : direction === 'left' ? 'gauche' : 'droite';

    if (nbGroupes > 1) {
      // Tronc commun — demander confirmation
      setConfirmMove({
        matiereNom,
        nbGroupes,
        sourceDate: sourceJour.date,
        sourceHeure: sourceHeureDebut,
        targetDate: targetJour.date,
        targetHeureDebut,
        targetHeureFin,
        direction: dirLabel,
      });
    } else {
      // Matière seule — déplacer directement
      executeArrowMove(creneauId, targetJour.date, targetHeureDebut, targetHeureFin);
    }
  };

  /**
   * Exécute le déplacement d'un seul créneau.
   */
  const executeArrowMove = async (
    creneauId: number,
    date: string,
    heureDebut: string,
    heureFin: string,
  ) => {
    setMovingArrow(true);
    try {
      await deplacerCreneau({
        creneau_id: creneauId,
        nouvelle_date: date,
        nouvelle_heure_debut: heureDebut,
        nouvelle_heure_fin: heureFin,
      }).unwrap();
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur lors du deplacement');
    } finally {
      setMovingArrow(false);
    }
  };

  /**
   * Exécute le déplacement groupé (tronc commun).
   */
  const executeGroupMove = async () => {
    if (!confirmMove) return;
    // Pré-check conflit : si la cible est déjà occupée par une autre matière
    // sur les mêmes groupes, ouvrir la modale de résolution au lieu de laisser
    // le backend refuser en 400 sec.
    const conflit = detecterConflit(
      confirmMove.matiereNom,
      confirmMove.sourceDate, confirmMove.sourceHeure,
      confirmMove.targetDate, confirmMove.targetHeureDebut,
    );
    if (conflit) {
      // Récupère la liste complète des groupes qui portent la matière source
      // (au-delà de ceux en conflit) — on veut swap ces colonnes-là entièrement.
      const jourSrc = data.jours.find(j => j.date === confirmMove.sourceDate);
      const creneauSrc = jourSrc?.creneaux.find(c => parseHeureDebut(c.heure_label) === confirmMove.sourceHeure);
      const groupeIds: number[] = [];
      if (creneauSrc) {
        for (const g of data.groupes) {
          const m = creneauSrc.matieres[g.id];
          if (m && m.matiere_nom === confirmMove.matiereNom) groupeIds.push(g.id);
        }
      }
      setConflitMove({
        matiereSource: confirmMove.matiereNom,
        nbGroupesSource: confirmMove.nbGroupes,
        sourceDate: confirmMove.sourceDate,
        sourceHeure: confirmMove.sourceHeure,
        matiereConflit: conflit.matiereConflit,
        targetDate: confirmMove.targetDate,
        targetHeureDebut: confirmMove.targetHeureDebut,
        targetHeureFin: confirmMove.targetHeureFin,
        slotLibreChoisi: '',
        groupeIds,
      });
      setConfirmMove(null);
      return;
    }
    setMovingArrow(true);
    try {
      await deplacerGroupe({
        matiere_nom: confirmMove.matiereNom,
        date_source: confirmMove.sourceDate,
        heure_source: confirmMove.sourceHeure,
        nouvelle_date: confirmMove.targetDate,
        nouvelle_heure_debut: confirmMove.targetHeureDebut,
        nouvelle_heure_fin: confirmMove.targetHeureFin,
        session_id: sessionId,
        niveau,
      }).unwrap();
      setConfirmMove(null);
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur lors du deplacement groupe');
    } finally {
      setMovingArrow(false);
    }
  };

  /**
   * Résolution conflit — option 1 : échanger source et matière conflit
   */
  const executeSwap = async () => {
    if (!conflitMove) return;
    setMovingArrow(true);
    try {
      await echangerGroupes({
        session_id: sessionId,
        niveau,
        matiere_a_nom: conflitMove.matiereSource,
        date_a: conflitMove.sourceDate,
        heure_a: conflitMove.sourceHeure,
        matiere_b_nom: conflitMove.matiereConflit,
        date_b: conflitMove.targetDate,
        heure_b: conflitMove.targetHeureDebut,
        // Passe les IDs des groupes à swapper pour ne toucher qu'aux colonnes
        // concernées (empêche la disparition des autres matières à la cible).
        groupe_ids: conflitMove.groupeIds,
      }).unwrap();
      setConflitMove(null);
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error || 'Échange échoué');
    } finally {
      setMovingArrow(false);
    }
  };

  /**
   * Résolution conflit — option 2 : déplacer la matière en conflit vers un
   * slot libre, puis déplacer la matière source vers la cible libérée.
   */
  const executeMoveConflitPuisSource = async () => {
    if (!conflitMove || !conflitMove.slotLibreChoisi) return;
    const [dateLibre, heureRange] = conflitMove.slotLibreChoisi.split('|');
    const [heureDebutLibre, heureFinLibre] = heureRange.split('-');
    setMovingArrow(true);
    try {
      // Étape 1 : déplacer la matière en conflit vers le slot libre
      await deplacerGroupe({
        matiere_nom: conflitMove.matiereConflit,
        date_source: conflitMove.targetDate,
        heure_source: conflitMove.targetHeureDebut,
        nouvelle_date: dateLibre,
        nouvelle_heure_debut: heureDebutLibre,
        nouvelle_heure_fin: heureFinLibre,
        session_id: sessionId,
        niveau,
      }).unwrap();
      // Étape 2 : déplacer la matière source vers la cible désormais libre
      await deplacerGroupe({
        matiere_nom: conflitMove.matiereSource,
        date_source: conflitMove.sourceDate,
        heure_source: conflitMove.sourceHeure,
        nouvelle_date: conflitMove.targetDate,
        nouvelle_heure_debut: conflitMove.targetHeureDebut,
        nouvelle_heure_fin: conflitMove.targetHeureFin,
        session_id: sessionId,
        niveau,
      }).unwrap();
      setConflitMove(null);
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error
        || 'Déplacement échoué (la matière en conflit a peut-être bougé mais pas la source)');
      // Rafraîchir pour refléter l'état réel de la BDD après la 1re étape
      onRefresh();
    } finally {
      setMovingArrow(false);
    }
  };

  const handleCellClick = (
    creneauId: number | undefined,
    matiereNom: string,
    isInactif: boolean = false,
    jourIdx?: number,
    creneauIdx?: number,
  ) => {
    if (!editMode || !creneauId || jourIdx === undefined || creneauIdx === undefined) return;
    const sourceJour = data.jours[jourIdx];
    const sourceCreneau = sourceJour?.creneaux[creneauIdx];
    if (!sourceJour || !sourceCreneau) return;
    const srcHeure = parseHeureDebut(sourceCreneau.heure_label);
    setSelectedCreneau({
      creneauId, matiereNom, isInactif,
      sourceDate: sourceJour.date,
      sourceHeure: srcHeure,
      nbShared: countSharedGroups(matiereNom, jourIdx, creneauIdx),
    });
    setMoveTarget(null);
    // Pré-remplir l'éditeur date/heure directe avec la position actuelle
    setDirectDate(sourceJour.date);
    const matchHeure = heureOptions.find(h => h.debut === srcHeure);
    setDirectHeure(matchHeure ? matchHeure.value : '');
  };

  /**
   * Déplace la matière vers une date+heure choisies directement (indépendamment
   * des slots du tableau). Utile pour ajouter/modifier le jour en dehors de la
   * plage affichée.
   */
  const handleMoveDirect = async () => {
    if (!selectedCreneau || !directDate || !directHeure) return;
    const [deb, fin] = directHeure.split('|');
    if (!deb || !fin) return;

    // Vérifier conflit sur la cible
    const conflit = detecterConflit(
      selectedCreneau.matiereNom,
      selectedCreneau.sourceDate,
      selectedCreneau.sourceHeure,
      directDate,
      deb,
    );
    if (conflit) {
      alert(`Le créneau cible (${directDate} ${deb}) est déjà occupé par « ${conflit.matiereConflit} ». Utilise "Déplacer vers" pour choisir un slot existant, ou libère d'abord la cible.`);
      return;
    }

    // Cas tronc commun → passer par deplacer_groupe (sinon on ne bouge qu'UN groupe)
    if (selectedCreneau.nbShared > 1) {
      if (!window.confirm(`Cette matière est présente dans ${selectedCreneau.nbShared} groupes (tronc commun). Déplacer TOUS les groupes vers ${directDate} ${deb}-${fin} ?`)) return;
      try {
        await deplacerGroupe({
          matiere_nom: selectedCreneau.matiereNom,
          date_source: selectedCreneau.sourceDate,
          heure_source: selectedCreneau.sourceHeure,
          nouvelle_date: directDate,
          nouvelle_heure_debut: deb,
          nouvelle_heure_fin: fin,
          session_id: sessionId,
          niveau,
        }).unwrap();
        setSelectedCreneau(null);
        setDirectDate(''); setDirectHeure('');
        onRefresh();
      } catch (err: any) {
        alert(err?.data?.error || 'Erreur lors du déplacement groupé');
      }
      return;
    }

    try {
      await deplacerCreneau({
        creneau_id: selectedCreneau.creneauId,
        nouvelle_date: directDate,
        nouvelle_heure_debut: deb,
        nouvelle_heure_fin: fin,
      }).unwrap();
      setSelectedCreneau(null);
      setDirectDate(''); setDirectHeure('');
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur lors du déplacement');
    }
  };

  const handleMove = async () => {
    if (!selectedCreneau || !moveTarget) return;
    const targetDeb = moveTarget.heure.split('-')[0];
    const targetFin = moveTarget.heure.split('-')[1];
    // Si la matière est partagée par plusieurs groupes (tronc commun ou
    // sous-ensemble type L2MI+L2EM+L2MT), on demande confirmation puis on
    // déplace tous les groupes ensemble via deplacer_groupe.
    if (selectedCreneau.nbShared > 1) {
      setConfirmMove({
        matiereNom: selectedCreneau.matiereNom,
        nbGroupes: selectedCreneau.nbShared,
        sourceDate: selectedCreneau.sourceDate,
        sourceHeure: selectedCreneau.sourceHeure,
        targetDate: moveTarget.date,
        targetHeureDebut: targetDeb,
        targetHeureFin: targetFin,
        direction: 'nouveau créneau',
      });
      // On ferme la barre pour éviter la confusion pendant la modale
      setSelectedCreneau(null);
      setMoveTarget(null);
      return;
    }
    try {
      await deplacerCreneau({
        creneau_id: selectedCreneau.creneauId,
        nouvelle_date: moveTarget.date,
        nouvelle_heure_debut: targetDeb,
        nouvelle_heure_fin: targetFin,
      }).unwrap();
      setSelectedCreneau(null);
      setMoveTarget(null);
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur lors du deplacement');
    }
  };

  const handleDelete = async (creneauId: number) => {
    if (!window.confirm('Supprimer ce créneau ?')) return;
    try {
      await deleteCreneau(creneauId).unwrap();
      setSelectedCreneau(null);
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleLock = async (creneauId: number) => {
    try {
      await verrouillerCreneau(creneauId).unwrap();
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleInactif = async (creneauId: number) => {
    try {
      await toggleInactif(creneauId).unwrap();
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  // Ouvrir le popup d'édition du formateur
  const handleEditFormateur = (e: React.MouseEvent, creneauId: number, matiereNom: string, enseignantNoms: string[]) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setEditingFormateur({
      creneauId,
      matiereNom,
      enseignantNoms: [...enseignantNoms],
      position: { top: rect.bottom + 4, left: Math.min(rect.left, window.innerWidth - 320) },
    });
    setSelectedFormateurId('');
  };

  // Retirer un formateur par index
  const handleRetirerFormateur = async (idx: number) => {
    if (!editingFormateur) return;
    try {
      const result = await modifierEnseignant({
        creneauId: editingFormateur.creneauId,
        retirer_index: idx,
      }).unwrap();
      setEditingFormateur({ ...editingFormateur, enseignantNoms: result.enseignant_noms });
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur');
    }
  };

  // Remplacer par un formateur sélectionné
  const handleRemplacerFormateur = async () => {
    if (!editingFormateur || !selectedFormateurId) return;
    try {
      const result = await modifierEnseignant({
        creneauId: editingFormateur.creneauId,
        enseignant_id: Number(selectedFormateurId),
      }).unwrap();
      setEditingFormateur({ ...editingFormateur, enseignantNoms: result.enseignant_noms });
      setSelectedFormateurId('');
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur');
    }
  };

  // Retirer tous les formateurs
  const handleRetirerTous = async () => {
    if (!editingFormateur) return;
    try {
      const result = await modifierEnseignant({
        creneauId: editingFormateur.creneauId,
        enseignant_id: null,
      }).unwrap();
      setEditingFormateur({ ...editingFormateur, enseignantNoms: result.enseignant_noms });
      onRefresh();
    } catch (err: any) {
      alert(err?.data?.error || 'Erreur');
    }
  };

  return (
    <div className="space-y-4">
      {/* Modal résolution de conflit — cible occupée par une autre matière */}
      {conflitMove && (() => {
        const slotsLibres = trouverSlotsLibres(conflitMove.matiereConflit);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className={`rounded-2xl shadow-2xl max-w-lg w-full p-6 ${
              isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle size={24} className="text-orange-500" />
                <h3 className="text-lg font-bold">Créneau déjà occupé</h3>
              </div>
              <p className="mb-4 text-sm">
                Vous voulez déplacer <strong>« {conflitMove.matiereSource} »</strong>{' '}
                vers un créneau qui contient déjà{' '}
                <strong>« {conflitMove.matiereConflit} »</strong>. Deux résolutions possibles :
              </p>

              {/* Option 1 : Swap */}
              <div className={`p-3 rounded-lg border mb-3 ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-sm font-semibold">Option 1 — Échanger les deux matières</div>
                  <button
                    onClick={executeSwap}
                    disabled={movingArrow}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 text-xs"
                  >
                    {movingArrow ? <Loader2 size={12} className="animate-spin" /> : <ArrowRightLeft size={12} />}
                    Échanger
                  </button>
                </div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  « {conflitMove.matiereSource} » prend la place de « {conflitMove.matiereConflit} »
                  et vice-versa (atomique, aucune donnée perdue).
                </p>
              </div>

              {/* Option 2 : Déplacer occupant vers slot libre */}
              <div className={`p-3 rounded-lg border mb-4 ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="text-sm font-semibold mb-2">
                  Option 2 — Déplacer « {conflitMove.matiereConflit} » vers un créneau libre
                </div>
                {slotsLibres.length === 0 ? (
                  <p className="text-xs text-orange-500">
                    Aucun créneau libre trouvé pour cette matière. Utilisez l'échange (option 1).
                  </p>
                ) : (
                  <div className="flex items-end gap-2">
                    <select
                      value={conflitMove.slotLibreChoisi}
                      onChange={(e) => setConflitMove({ ...conflitMove, slotLibreChoisi: e.target.value })}
                      className={`flex-1 px-3 py-2 rounded-lg border text-xs ${
                        isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
                      }`}
                    >
                      <option value="">— Choisir un créneau libre —</option>
                      {slotsLibres.map((s) => (
                        <option key={`${s.date}|${s.heureDebut}-${s.heureFin}`}
                          value={`${s.date}|${s.heureDebut}-${s.heureFin}`}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={executeMoveConflitPuisSource}
                      disabled={movingArrow || !conflitMove.slotLibreChoisi}
                      className="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 text-xs whitespace-nowrap"
                    >
                      {movingArrow ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      Déplacer puis positionner
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setConflitMove(null)}
                  className={`px-4 py-2 rounded-lg text-sm ${
                    isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal confirmation tronc commun */}
      {confirmMove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 ${
            isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <Layers size={24} className="text-amber-500" />
              <h3 className="text-lg font-bold">Matiere en tronc commun</h3>
            </div>
            <p className="mb-4">
              La matiere <strong>« {confirmMove.matiereNom} »</strong> est partagee par{' '}
              <strong>{confirmMove.nbGroupes} groupes</strong> sur ce creneau.
            </p>
            <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Voulez-vous deplacer toutes les matieres ensemble vers le{' '}
              {confirmMove.direction} ?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmMove(null)}
                className={`px-4 py-2 rounded-lg text-sm ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                } transition`}
              >
                Annuler
              </button>
              <button
                onClick={executeGroupMove}
                disabled={movingArrow}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm transition"
              >
                {movingArrow ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />}
                Deplacer les {confirmMove.nbGroupes} groupes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal déplacement par select */}
      {selectedCreneau && editMode && (
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-750 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <ArrowRightLeft size={16} className="text-blue-500" />
              <span className="font-medium text-sm">
                Modifier : <strong>{selectedCreneau.matiereNom}</strong>
              </span>
              {selectedCreneau.nbShared > 1 && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded ${
                  isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'
                }`}
                  title={`Cette matière est partagée par ${selectedCreneau.nbShared} groupes. Le déplacement s'appliquera à tous.`}
                >
                  <Layers size={11} /> {selectedCreneau.nbShared} groupes (tronc commun)
                </span>
              )}
            </div>
            <button onClick={() => setSelectedCreneau(null)} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs mb-1">Déplacer vers</label>
              <select
                value={moveTarget ? `${moveTarget.date}|${moveTarget.heure}` : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const [d, h] = e.target.value.split('|');
                    const slot = availableSlots.find(s => s.date === d && `${s.heure_debut}-${s.heure_fin}` === h);
                    if (slot) setMoveTarget({ date: d, heure: h });
                  }
                }}
                className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
              >
                <option value="">Choisir un créneau...</option>
                {availableSlots.map((slot) => (
                  <option key={`${slot.date}-${slot.heure_debut}`} value={`${slot.date}|${slot.heure_debut}-${slot.heure_fin}`}>
                    {slot.heure_label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleMove}
              disabled={!moveTarget || moving}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {moving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Déplacer
            </button>

            {/* Séparateur visuel + édition directe date/heure */}
            <div className={`h-8 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
            <div>
              <label className="block text-xs mb-1">Changer la date</label>
              <input
                type="date"
                value={directDate}
                onChange={(e) => setDirectDate(e.target.value)}
                className={`px-2 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Heure</label>
              <select
                value={directHeure}
                onChange={(e) => setDirectHeure(e.target.value)}
                className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
              >
                <option value="">Choisir…</option>
                {heureOptions.map((h) => (
                  <option key={h.value} value={h.value}>{h.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleMoveDirect}
              disabled={!directDate || !directHeure || moving}
              className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
              title="Déplace vers la date + heure choisies (même hors des slots affichés)"
            >
              {moving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Appliquer date/heure
            </button>

            <button
              onClick={() => handleToggleLock(selectedCreneau.creneauId)}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' : 'bg-yellow-50 hover:bg-yellow-100 text-yellow-700'}`}
            >
              <Lock size={14} />
              Verrouiller
            </button>

            <button
              onClick={() => handleToggleInactif(selectedCreneau.creneauId)}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${
                selectedCreneau.isInactif
                  ? (isDark ? 'bg-green-900/30 hover:bg-green-800/40 text-green-400' : 'bg-green-50 hover:bg-green-100 text-green-700')
                  : (isDark ? 'bg-gray-700 hover:bg-gray-600 text-purple-400' : 'bg-purple-50 hover:bg-purple-100 text-purple-700')
              }`}
            >
              {selectedCreneau.isInactif ? <Eye size={14} /> : <EyeOff size={14} />}
              {selectedCreneau.isInactif ? 'Réactiver la matière' : 'Désactiver la matière'}
            </button>

            <button
              onClick={() => handleDelete(selectedCreneau.creneauId)}
              className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              <Trash2 size={14} />
              Supprimer
            </button>
          </div>
        </div>
      )}

      {/* Popup edition formateur */}
      {editingFormateur && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setEditingFormateur(null)}
        >
          <div
            className={`absolute z-50 rounded-xl shadow-2xl border p-4 w-80 ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}
            style={{ top: editingFormateur.position.top, left: editingFormateur.position.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold flex items-center gap-1.5">
                <Users size={14} className="text-blue-500" />
                Formateurs — {editingFormateur.matiereNom}
              </h4>
              <button onClick={() => setEditingFormateur(null)} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>

            {/* Liste des formateurs actuels */}
            {editingFormateur.enseignantNoms.length > 0 ? (
              <div className="space-y-1.5 mb-3">
                {editingFormateur.enseignantNoms.map((nom, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-sm ${
                      isDark ? 'bg-gray-800' : 'bg-gray-50'
                    }`}
                  >
                    <span className="truncate">{nom}</span>
                    <button
                      onClick={() => handleRetirerFormateur(idx)}
                      className="shrink-0 ml-2 p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition"
                      title="Retirer ce formateur"
                    >
                      <UserMinus size={13} />
                    </button>
                  </div>
                ))}
                {editingFormateur.enseignantNoms.length > 1 && (
                  <button
                    onClick={handleRetirerTous}
                    className="w-full text-xs text-red-500 hover:text-red-600 py-1"
                  >
                    Retirer tous les formateurs
                  </button>
                )}
              </div>
            ) : (
              <p className={`text-xs mb-3 italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Aucun formateur attribue
              </p>
            )}

            {/* Sélecteur pour remplacer/ajouter */}
            <div className="space-y-2">
              <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {editingFormateur.enseignantNoms.length > 0 ? 'Remplacer par' : 'Attribuer un formateur'}
              </label>
              <select
                value={selectedFormateurId}
                onChange={(e) => setSelectedFormateurId(e.target.value)}
                className={`w-full px-2.5 py-2 text-sm border rounded-lg ${
                  isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">-- Choisir un formateur --</option>
                {(formateurs as Formateur[]).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nom_complet || `${f.nom} ${f.prenoms}`}
                    {f.specialite ? ` (${f.specialite})` : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={handleRemplacerFormateur}
                disabled={!selectedFormateurId}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <UserPlus size={14} />
                {editingFormateur.enseignantNoms.length > 0 ? 'Remplacer' : 'Attribuer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className={`${printMode ? '' : 'overflow-x-auto'}`}>
        <table className={`w-full border-collapse border ${borderColor} ${textColor}`} style={{ fontSize: printMode ? '10pt' : undefined }}>
          <thead>
            <tr>
              <th className={`border ${borderColor} ${headerBg} px-3 py-2 text-left font-bold`} style={{ width: '100px' }}>
                DATES
              </th>
              <th className={`border ${borderColor} ${headerBg} px-3 py-2 text-center font-bold`} style={{ width: '80px' }}>
                HORAIRE
              </th>
              {data.groupes.map((groupe) => (
                <th
                  key={groupe.id}
                  className={`border ${borderColor} ${headerBg} px-3 py-2 text-center font-bold`}
                >
                  {groupe.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.jours.map((jour, jourIdx) => (
              jour.creneaux.map((creneau, creneauIdx) => (
                <tr key={`${jour.date}-${creneau.heure_label}`}>
                  {/* Date - seulement sur le premier créneau du jour */}
                  {creneauIdx === 0 && (
                    <td
                      className={`border ${borderColor} px-3 py-2 font-bold text-center ${cellBg}`}
                      rowSpan={jour.creneaux.length}
                    >
                      <div className="font-bold">{jour.jour_semaine}</div>
                      <div className="text-sm">{jour.date_label}</div>
                      {editMode && !printMode && (
                        <button
                          onClick={() => setHorairesJourCible(jour)}
                          title="Modifier les horaires de ce jour"
                          className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded transition-colors ${
                            isDark
                              ? 'bg-gray-700 hover:bg-blue-900/50 text-gray-300 hover:text-blue-300'
                              : 'bg-gray-100 hover:bg-blue-100 text-gray-600 hover:text-blue-700'
                          }`}
                        >
                          <Clock size={10} />
                          Horaires
                        </button>
                      )}
                    </td>
                  )}

                  {/* Horaire */}
                  <td className={`border ${borderColor} px-3 py-2 text-center font-semibold ${cellBg}`}>
                    {creneau.heure_label}
                  </td>

                  {/* Matières par groupe */}
                  {data.groupes.map((groupe) => {
                    const matiereData = creneau.matieres[groupe.id];
                    const isSelected = selectedCreneau && matiereData?.creneau_id === selectedCreneau.creneauId;

                    // Déterminer si la matière est en tronc commun
                    const nbShared = matiereData ? countSharedGroups(matiereData.matiere_nom, jourIdx, creneauIdx) : 0;
                    const isTroncCommun = nbShared > 1;

                    // Déterminer les directions possibles
                    const canUp = getTargetSlot(jourIdx, creneauIdx, 'up') !== null;
                    const canDown = getTargetSlot(jourIdx, creneauIdx, 'down') !== null;
                    const canLeft = getTargetSlot(jourIdx, creneauIdx, 'left') !== null;
                    const canRight = getTargetSlot(jourIdx, creneauIdx, 'right') !== null;

                    return (
                      <td
                        key={groupe.id}
                        onClick={() => matiereData && handleCellClick(matiereData.creneau_id, matiereData.matiere_nom, matiereData.est_inactif, jourIdx, creneauIdx)}
                        className={`border ${borderColor} px-2 py-1.5 text-center ${cellBg} group relative ${
                          editMode && matiereData
                            ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition'
                            : ''
                        } ${isSelected ? (isDark ? 'bg-blue-900/40 ring-2 ring-blue-500' : 'bg-blue-100 ring-2 ring-blue-400') : ''}`}
                      >
                        {matiereData ? (
                          <div className="relative">
                            {/* Nom de la matière */}
                            <div className={`font-medium text-sm ${matiereData.est_inactif ? 'line-through opacity-40' : ''}`}>
                              {matiereData.matiere_nom}
                            </div>

                            {/* Badge durée personnalisée */}
                            {matiereData.duree_label && (
                              <div className={`inline-flex items-center gap-0.5 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                isDark ? 'bg-orange-800/60 text-orange-200' : 'bg-orange-100 text-orange-700'
                              }`} title={`Durée personnalisée : ${matiereData.duree_label}`}>
                                {matiereData.duree_label}
                                {matiereData.heure_fin && (
                                  <span className="font-normal opacity-70 ml-0.5">({matiereData.heure_fin})</span>
                                )}
                              </div>
                            )}

                            {/* Badge tronc commun */}
                            {isTroncCommun && editMode && (
                              <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                                isDark ? 'bg-amber-700 text-amber-100' : 'bg-amber-200 text-amber-800'
                              }`} title={`Tronc commun : ${nbShared} groupes`}>
                                {nbShared}
                              </div>
                            )}

                            {/* Statut inactif */}
                            {matiereData.est_inactif && (
                              <div className="text-xs text-purple-500 mt-0.5 flex items-center justify-center gap-1">
                                <EyeOff size={10} /> Inactif
                              </div>
                            )}

                            {/* Enseignants (cliquable pour editer) */}
                            {showEnseignants && !matiereData.est_inactif && (
                              <div
                                className={`text-xs mt-0.5 italic cursor-pointer hover:underline ${
                                  matiereData.enseignant_noms.length > 0
                                    ? subTextColor
                                    : 'text-orange-400 dark:text-orange-500 not-italic'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (matiereData.creneau_id) {
                                    handleEditFormateur(e, matiereData.creneau_id, matiereData.matiere_nom, matiereData.enseignant_noms);
                                  }
                                }}
                                title="Cliquer pour modifier le formateur"
                              >
                                {matiereData.enseignant_noms.length > 0
                                  ? Array.from(new Set(matiereData.enseignant_noms)).join(', ')
                                  : '+ formateur'
                                }
                              </div>
                            )}

                            {/* Flèches de déplacement (mode édition) */}
                            {editMode && !printMode && matiereData.creneau_id && !matiereData.est_inactif && (
                              <div className="flex items-center justify-center gap-0.5 mt-1 opacity-0 group-hover:opacity-100 transition">
                                {/* Gauche (jour précédent) */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleArrowMove(matiereData.matiere_nom, matiereData.creneau_id!, jourIdx, creneauIdx, 'left');
                                  }}
                                  disabled={!canLeft || movingArrow}
                                  className={`p-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-800 disabled:opacity-20 disabled:cursor-not-allowed transition ${
                                    isDark ? 'text-blue-400' : 'text-blue-600'
                                  }`}
                                  title="Jour precedent"
                                >
                                  <ChevronLeft size={14} />
                                </button>

                                {/* Haut */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleArrowMove(matiereData.matiere_nom, matiereData.creneau_id!, jourIdx, creneauIdx, 'up');
                                  }}
                                  disabled={!canUp || movingArrow}
                                  className={`p-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-800 disabled:opacity-20 disabled:cursor-not-allowed transition ${
                                    isDark ? 'text-blue-400' : 'text-blue-600'
                                  }`}
                                  title="Creneau precedent"
                                >
                                  <ChevronUp size={14} />
                                </button>

                                {/* Bas */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleArrowMove(matiereData.matiere_nom, matiereData.creneau_id!, jourIdx, creneauIdx, 'down');
                                  }}
                                  disabled={!canDown || movingArrow}
                                  className={`p-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-800 disabled:opacity-20 disabled:cursor-not-allowed transition ${
                                    isDark ? 'text-blue-400' : 'text-blue-600'
                                  }`}
                                  title="Creneau suivant"
                                >
                                  <ChevronDown size={14} />
                                </button>

                                {/* Droite (jour suivant) */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleArrowMove(matiereData.matiere_nom, matiereData.creneau_id!, jourIdx, creneauIdx, 'right');
                                  }}
                                  disabled={!canRight || movingArrow}
                                  className={`p-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-800 disabled:opacity-20 disabled:cursor-not-allowed transition ${
                                    isDark ? 'text-blue-400' : 'text-blue-600'
                                  }`}
                                  title="Jour suivant"
                                >
                                  <ChevronRight size={14} />
                                </button>
                              </div>
                            )}

                            {/* Bouton activer/désactiver */}
                            {!printMode && !editMode && matiereData.creneau_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleInactif(matiereData.creneau_id!);
                                }}
                                className={`mt-1 px-2 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1 transition opacity-0 group-hover:opacity-100 ${
                                  matiereData.est_inactif
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                }`}
                                title={matiereData.est_inactif ? 'Réactiver cette matière' : 'Désactiver cette matière'}
                              >
                                {matiereData.est_inactif ? <Eye size={11} /> : <EyeOff size={11} />}
                                {matiereData.est_inactif ? 'Réactiver' : 'Désactiver'}
                              </button>
                            )}
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal modification horaires d'un jour */}
      {horairesJourCible && (
        <ModifierHorairesJourModal
          sessionId={sessionId}
          isDark={isDark}
          jour={horairesJourCible}
          onClose={() => setHorairesJourCible(null)}
          onSuccess={() => { setHorairesJourCible(null); onRefresh(); }}
        />
      )}
    </div>
  );
};

// ─── Modal Ajout Matière ────────────────────────────────────────────

interface AjouterMatiereModalProps {
  sessionId: number;
  niveau: string;
  isDark: boolean;
  jours: { date: string; date_label: string; jour_semaine: string; creneaux: { heure_label: string; heure_debut?: string; heure_fin?: string }[] }[];
  onClose: () => void;
  onSuccess: () => void;
}

const AjouterMatiereModal: React.FC<AjouterMatiereModalProps> = ({
  sessionId, niveau, isDark, jours, onClose, onSuccess,
}) => {
  const [fetchManquantes, { data: manquantesData, isLoading: loadingManquantes }] = useLazyGetMatiereManquantesQuery();
  const [ajouterMatiere, { isLoading: ajoutEnCours }] = useAjouterMatiereMutation();

  const [search, setSearch] = useState('');
  const [selectedMatiere, setSelectedMatiere] = useState<MatiereManquante | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedHeure, setSelectedHeure] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  // Charger les matières manquantes à chaque ouverture du modal (forcer re-fetch)
  React.useEffect(() => {
    fetchManquantes({ sessionId, niveau }, false);
  }, [fetchManquantes, sessionId, niveau]);

  const manquantes = manquantesData?.matieres_manquantes || [];
  const filtered = manquantes.filter((m) => {
    const q = search.toLowerCase();
    return !q || m.nom.toLowerCase().includes(q) || m.code.toLowerCase().includes(q) || m.options.some(o => o.toLowerCase().includes(q));
  });

  // Parser heure_label "08H-10H" → heure_debut/heure_fin
  const parseHeure = (label: string) => {
    const parts = label.replace(/H/g, ':').replace(/-/g, '|').split('|');
    if (parts.length === 2) {
      const debut = parts[0].trim().replace(/:$/, ':00');
      const fin = parts[1].trim().replace(/:$/, ':00');
      return { debut, fin };
    }
    return null;
  };

  const handleAjouter = async () => {
    if (!selectedMatiere || !selectedDate || !selectedHeure) {
      setMessage({ type: 'error', text: 'Sélectionnez une matière, un jour et un créneau.' });
      return;
    }

    const heures = parseHeure(selectedHeure);
    if (!heures) {
      setMessage({ type: 'error', text: 'Format horaire invalide.' });
      return;
    }

    try {
      const payload: any = {
        session_id: sessionId,
        date: selectedDate,
        heure_debut: heures.debut,
        heure_fin: heures.fin,
      };

      if (selectedMatiere.matiere_id) {
        payload.matiere_id = selectedMatiere.matiere_id;
      } else {
        // Matière hors table matieres — envoi par nom
        payload.matiere_nom = selectedMatiere.nom;
        payload.niveau = selectedMatiere.niveau;
        if (selectedMatiere.groupes_manquants?.length) {
          payload.groupe_ids = selectedMatiere.groupes_manquants.map(g => g.id);
        }
      }

      const result = await ajouterMatiere(payload).unwrap();

      if (result.warning) {
        setMessage({ type: 'warning', text: result.message });
      } else if (result.conflits) {
        const details = result.conflits.map(c => `${c.groupe}: ${c.matiere_existante}`).join(', ');
        setMessage({ type: 'error', text: `${result.message} — ${details}` });
      } else {
        setMessage({
          type: 'success',
          text: `${result.message}${result.est_en_commun ? ` (matière en commun — ${result.groupes_affectes?.map(g => g.label).join(', ')})` : ''}`,
        });
        setTimeout(onSuccess, 1500);
      }
    } catch (err: any) {
      const detail = err?.data?.message || err?.data?.error || 'Erreur lors de l\'ajout';
      const conflits = err?.data?.conflits;
      if (conflits) {
        const details = conflits.map((c: any) => `${c.groupe}: ${c.matiere_existante}`).join(', ');
        setMessage({ type: 'error', text: `Conflit horaire — ${details}` });
      } else {
        setMessage({ type: 'error', text: detail });
      }
    }
  };

  const bg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDark ? 'text-gray-100' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-800'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className={`w-full max-w-2xl max-h-[85vh] rounded-xl shadow-2xl border ${bg} flex flex-col`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-lg font-semibold ${textPrimary} flex items-center gap-2`}>
            <Plus size={20} className="text-green-500" />
            Ajouter une matière à l'emploi du temps
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">
            <X size={20} className={textSecondary} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Message */}
          {message && (
            <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${
              message.type === 'success' ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
              message.type === 'warning' ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
              'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            }`}>
              {message.type === 'warning' ? <AlertTriangle size={16} className="shrink-0 mt-0.5" /> :
               message.type === 'error' ? <AlertCircle size={16} className="shrink-0 mt-0.5" /> :
               <Check size={16} className="shrink-0 mt-0.5" />}
              {message.text}
            </div>
          )}

          {/* Recherche */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textPrimary}`}>
              Matière à ajouter {manquantesData && <span className={textSecondary}>({manquantesData.total} manquante{manquantesData.total > 1 ? 's' : ''})</span>}
            </label>
            <div className="relative">
              <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, code ou option..."
                className={`${inputCls} pl-9`}
              />
            </div>
          </div>

          {/* Liste des matières manquantes */}
          <div className={`border rounded-lg overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            {loadingManquantes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin" size={24} />
              </div>
            ) : filtered.length === 0 ? (
              <div className={`py-8 text-center text-sm ${textSecondary}`}>
                {manquantes.length === 0 ? 'Toutes les matières sont déjà dans l\'emploi du temps' : 'Aucun résultat'}
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.map((m) => {
                  const mKey = m.matiere_id ? `id-${m.matiere_id}` : `nom-${m.nom}-${m.niveau}`;
                  const isSelected = selectedMatiere?.nom === m.nom && selectedMatiere?.niveau === m.niveau;
                  return (
                  <button
                    key={mKey}
                    onClick={() => { setSelectedMatiere(m); setMessage(null); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between gap-2 ${
                      isSelected
                        ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-800'
                        : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div>
                      <span className={`font-medium ${isSelected ? '' : textPrimary}`}>
                        {m.nom}
                      </span>
                      {m.code && <span className={`ml-2 text-xs ${textSecondary}`}>{m.code}</span>}
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        <span className={`text-xs ${textSecondary}`}>{m.options.join(', ')}</span>
                        {m.groupes_manquants && m.groupes_manquants.length > 0 && (
                          <span className="text-xs text-orange-500 dark:text-orange-400 font-medium">
                            (manque: {m.groupes_manquants.map(g => g.label).join(', ')})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {m.niveau}
                      </span>
                      {m.est_en_commun && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          <Layers size={10} />
                          Commun
                        </span>
                      )}
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Matière sélectionnée : info */}
          {selectedMatiere?.est_en_commun && (
            <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${isDark ? 'bg-amber-900/20 text-amber-300' : 'bg-amber-50 text-amber-800'}`}>
              <Info size={16} className="shrink-0 mt-0.5" />
              <div>
                <strong>{selectedMatiere.nom}</strong> est une matière en commun partagée entre : <strong>{selectedMatiere.options.join(', ')}</strong>.
                Le système créera automatiquement les créneaux pour tous les groupes concernés.
              </div>
            </div>
          )}

          {/* Sélection du créneau horaire */}
          {selectedMatiere && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${textPrimary}`}>Jour</label>
                <select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className={inputCls}
                >
                  <option value="">— Choisir un jour —</option>
                  {jours.map((j) => (
                    <option key={j.date} value={j.date}>
                      {j.jour_semaine} {j.date_label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${textPrimary}`}>Créneau horaire</label>
                <select
                  value={selectedHeure}
                  onChange={(e) => setSelectedHeure(e.target.value)}
                  className={inputCls}
                >
                  <option value="">— Choisir un créneau —</option>
                  {jours[0]?.creneaux?.map((c) => (
                    <option key={c.heure_label} value={c.heure_label}>
                      {c.heure_label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm rounded-lg border transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            Annuler
          </button>
          <button
            onClick={handleAjouter}
            disabled={!selectedMatiere || !selectedDate || !selectedHeure || ajoutEnCours}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {ajoutEnCours ? (
              <><Loader2 size={16} className="animate-spin" /> Ajout en cours…</>
            ) : (
              <><Plus size={16} /> Ajouter la matière</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal fusion de groupes d'options ───────────────────────────────────────

interface FusionGroupesModalProps {
  groupes: { id: number; label: string }[];
  isDark: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FusionGroupesModal: React.FC<FusionGroupesModalProps> = ({ groupes, isDark, onClose, onSuccess }) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [fusionner, { isLoading }] = useFusionnerGroupesMutation();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleFusion = async () => {
    if (selected.size < 2) return;
    try {
      const result = await fusionner({ groupe_ids: Array.from(selected) }).unwrap();
      setMessage({ type: 'success', text: result.message });
      setTimeout(onSuccess, 1500);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || 'Erreur lors de la fusion' });
    }
  };

  const bg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDark ? 'text-gray-100' : 'text-gray-900';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className={`w-full max-w-md rounded-xl shadow-2xl border ${bg} flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-lg font-semibold ${textPrimary} flex items-center gap-2`}>
            <Layers size={20} className="text-purple-500" />
            Fusionner des groupes
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {message && (
            <div className={`p-3 rounded-lg text-sm ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            }`}>
              {message.text}
            </div>
          )}

          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Selectionnez les groupes a fusionner (min. 2). Les creneaux seront regroupes dans une seule colonne.
          </p>

          <div className={`border rounded-lg divide-y ${isDark ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-200'}`}>
            {groupes.map((g) => (
              <label
                key={g.id}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                  selected.has(g.id)
                    ? isDark ? 'bg-purple-900/20' : 'bg-purple-50'
                    : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(g.id)}
                  onChange={() => toggle(g.id)}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className={`text-sm font-medium ${textPrimary}`}>{g.label}</span>
              </label>
            ))}
          </div>

          <button
            onClick={handleFusion}
            disabled={selected.size < 2 || isLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
          >
            <Layers size={16} />
            {isLoading ? 'Fusion en cours...' : `Fusionner ${selected.size} groupe${selected.size > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal Modifier Horaires d'un Jour ─────────────────────────────

interface ModifierHorairesJourModalProps {
  sessionId: number;
  isDark: boolean;
  jour: { date: string; date_label: string; jour_semaine: string; creneaux: { heure_label: string }[] };
  onClose: () => void;
  onSuccess: () => void;
}

/** "08H-10H" ou "08H30-10H30" → { debut: "08:00" ou "08:30", fin: "10:00" ou "10:30" } */
function parseHeureLabel(label: string): { debut: string; fin: string } | null {
  const m = label.match(/^(\d{1,2})H(\d{0,2})\s*-\s*(\d{1,2})H(\d{0,2})$/);
  if (!m) return null;
  const pad = (n: string) => n.padStart(2, '0');
  const dH = pad(m[1]);
  const dM = m[2] ? pad(m[2]) : '00';
  const fH = pad(m[3]);
  const fM = m[4] ? pad(m[4]) : '00';
  return { debut: `${dH}:${dM}`, fin: `${fH}:${fM}` };
}

const ModifierHorairesJourModal: React.FC<ModifierHorairesJourModalProps> = ({
  sessionId, isDark, jour, onClose, onSuccess,
}) => {
  const [modifierHorairesJour, { isLoading }] = useModifierHorairesJourMutation();

  // Initialiser : pour chaque créneau du jour, parser le heure_label en {debut, fin}
  const initial = jour.creneaux.map((c) => {
    const parsed = parseHeureLabel(c.heure_label);
    return {
      ancienne_heure_debut: parsed?.debut || '08:00',
      label: c.heure_label,
      nouvelle_heure_debut: parsed?.debut || '08:00',
      nouvelle_heure_fin: parsed?.fin || '10:00',
    };
  });

  const [rows, setRows] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const updateRow = (idx: number, field: 'nouvelle_heure_debut' | 'nouvelle_heure_fin', value: string) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    // Vérifier qu'il y a au moins un changement
    const mappings = rows
      .filter((r) => {
        const oldFin = parseHeureLabel(r.label)?.fin || '';
        return r.nouvelle_heure_debut !== r.ancienne_heure_debut || r.nouvelle_heure_fin !== oldFin;
      })
      .map((r) => ({
        ancienne_heure_debut: r.ancienne_heure_debut,
        nouvelle_heure_debut: r.nouvelle_heure_debut,
        nouvelle_heure_fin: r.nouvelle_heure_fin,
      }));

    if (mappings.length === 0) {
      setError('Aucun changement détecté.');
      return;
    }

    // Vérifier doublons côté client
    const seen = new Set<string>();
    for (const m of mappings) {
      const key = `${m.nouvelle_heure_debut}-${m.nouvelle_heure_fin}`;
      if (seen.has(key)) {
        setError(`Horaire cible dupliqué : ${key}`);
        return;
      }
      seen.add(key);
      if (m.nouvelle_heure_debut >= m.nouvelle_heure_fin) {
        setError(`Heure début ${m.nouvelle_heure_debut} doit être < heure fin ${m.nouvelle_heure_fin}`);
        return;
      }
    }

    try {
      const res = await modifierHorairesJour({
        session_id: sessionId,
        date: jour.date,
        mappings,
      }).unwrap();
      setSuccess(res.message);
      setTimeout(() => {
        onSuccess();
      }, 800);
    } catch (e: any) {
      setError(e?.data?.error || e?.message || 'Erreur lors de la modification');
    }
  };

  const bgModal = isDark ? 'bg-gray-800' : 'bg-white';
  const borderModal = isDark ? 'border-gray-700' : 'border-gray-200';
  const textColor = isDark ? 'text-gray-100' : 'text-gray-900';
  const subText = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${bgModal} rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border ${borderModal}`}>
        <div className={`flex items-center justify-between p-4 border-b ${borderModal}`}>
          <div>
            <h3 className={`font-semibold ${textColor}`}>Modifier les horaires du jour</h3>
            <p className={`text-xs ${subText} mt-0.5`}>{jour.jour_semaine} {jour.date_label}</p>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${textColor}`}
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className={`text-xs ${subText}`}>
            Décale en bloc tous les créneaux de ce jour vers de nouveaux horaires.
            Les créneaux verrouillés sont préservés.
          </p>

          <div className="space-y-2">
            {rows.map((row, idx) => (
              <div key={idx} className={`p-2.5 rounded border ${borderModal}`}>
                <div className={`text-xs font-medium ${subText} mb-1.5`}>
                  Créneau actuel : <span className={textColor}>{row.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className={`block text-[10px] ${subText} mb-0.5`}>Nouveau début</label>
                    <input
                      type="time"
                      value={row.nouvelle_heure_debut}
                      onChange={(e) => updateRow(idx, 'nouvelle_heure_debut', e.target.value)}
                      className={`w-full px-2 py-1.5 text-sm rounded border ${inputBg}`}
                    />
                  </div>
                  <span className={`pt-4 ${subText}`}>→</span>
                  <div className="flex-1">
                    <label className={`block text-[10px] ${subText} mb-0.5`}>Nouvelle fin</label>
                    <input
                      type="time"
                      value={row.nouvelle_heure_fin}
                      onChange={(e) => updateRow(idx, 'nouvelle_heure_fin', e.target.value)}
                      className={`w-full px-2 py-1.5 text-sm rounded border ${inputBg}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
          {success && (
            <div className="p-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-xs text-green-700 dark:text-green-300">
              {success}
            </div>
          )}
        </div>

        <div className={`flex justify-end gap-2 p-4 border-t ${borderModal}`}>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`px-3 py-1.5 text-sm rounded border ${borderModal} ${textColor} disabled:opacity-50`}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50"
          >
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {isLoading ? 'Modification...' : 'Appliquer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmploiTempsTableau;
